import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getGvcCsv, runGdpb, runMlPremium, runMlMinPrice, runMlPrice, runAmzPrice, runAmzStock, runAmzFiscal, runShopeeAttrs, runShopeePrice, runSyncImages, runMlClose, runMlFiscal, runNvsPrice, runNvsImages, runNvsAttrs, runNvsCategory, runNvsSubcategories, runBlingDescriptions, runBlingPrice, runProductName, runNvsDescription, runNvsEan } from "../api/client";
import JobLog from "../components/JobLog";

// ─── Definição de grupos de colunas ───────────────────────────────────────────

// Prefixo de canal nas mensagens de divergência (campo "field" → "[CANAL]")
// Formato do backend: "[ERRO] [MLC] Preço do canal está acima do esperado"
// Mercado Livre é um grupo só (Clássico + Premium) — aceita lista de prefixos.
const CHANNEL_PREFIX = {
  mlc:    ["[MLC]", "[MLP]"],
  sp_ml:  ["[SP_MLC]", "[SP_MLP]", "[SP_ML]"],
  shopee: "[SHOPEE]",
  amazon: "[AMAZON]",
  nvs:    "[NVS]",
};

// Colunas do canal Mercado Livre (Clássico + Premium). O canal SP-ML (segunda conta ML, SKUs
// EGSP*) tem as MESMAS colunas com prefixo sp_ — geradas daqui por toSpKey, sem duplicar.
const ML_COLS = [
      { key: "sku",                      label: "SKU",          fixed: true },
      { key: "overall_status",           label: "Status" },
      { key: "grupo_preco",              label: "Grupo" },
      { key: "mlc_found",                label: "Clássico",     type: "bool" },
      { key: "mlp_found",                label: "Premium",      type: "bool" },
      { key: "mlc_buy_box",              label: "Destaque",     type: "simNao" },
      { key: "estoque_bling",            label: "Estq Bling" },
      { key: "mlc_stock",                label: "Clás. Estq" },
      { key: "estoque_divergente_mlc",   label: "Clás. Estq Div", type: "simNao" },
      { key: "mlc_price_expected",       label: "Preço Esp." },
      { key: "mlc_price_actual",         label: "Clás. Preço" },
      { key: "mlc_price_divergente",     label: "Clás. Preço Div", type: "simNao" },
      { key: "mlc_min_price_expected",   label: "Mín Esp." },
      { key: "mlc_min_price_actual",     label: "Clás. Mín" },
      { key: "mlc_price_automatic",      label: "Clás. Automático", type: "simNao" },
      { key: "mlc_min_price_divergente", label: "Clás. Mín Div", type: "simNao" },
      { key: "mlc_fiscal_data_ok",       label: "Dados Fiscais", type: "simNao" },
      { key: "mlp_stock",                label: "Prem. Estq" },
      { key: "estoque_divergente_mlp",   label: "Prem. Estq Div", type: "simNao" },
      { key: "mlp_price_actual",         label: "Prem. Preço" },
      { key: "mlp_price_divergente",     label: "Prem. Preço Div", type: "simNao" },
      { key: "mlp_min_price_actual",     label: "Prem. Mín" },
      { key: "mlp_price_automatic",      label: "Prem. Automático", type: "simNao" },
      { key: "mlp_min_price_divergente", label: "Prem. Mín Div", type: "simNao" },
];

// Colunas do Full do Mercado Livre — só o grupo SP-ML tem (o depósito "ML Full" é da filial
// SP). "A caminho" é derivado (depósito do Bling − recebido no Full): o ML não informa envio
// em trânsito. "Full Div" só acusa o excesso do lado do ML; envio aberto não é divergência.
const SP_FULL_COLS = [
      { key: "sp_full_logistica",        label: "Logística ML" },
      { key: "sp_full_bling",            label: "Full Bling" },
      { key: "sp_full_ml",               label: "Full ML" },
      { key: "sp_full_disponivel",       label: "Full Disp." },
      { key: "sp_full_a_caminho",        label: "Full A Caminho" },
      { key: "sp_full_divergente",       label: "Full Div",     type: "simNao" },
];

// SKUs EGSP* são da conta SP-ML (segunda conta do Mercado Livre); os demais, do ML principal.
// Cada um só aparece no grupo do seu canal (ver `rowFilter` nos grupos abaixo):
//   Mercado Livre e NVS → só matriz | SP-ML e Shopee → só EGSP* | Amazon → AS DUAS
// A Amazon passou a valer pras duas filiais em 2026-10-30 (matriz FBA, EGSP* FBM), então a aba
// dela não filtra por filial. A NVS segue sendo o único canal só da matriz.
const isEgsp = (sku) => String(sku ?? "").trim().toUpperCase().startsWith("EGSP");

const toSpKey = (k) => {
  if (k.startsWith("mlc_") || k.startsWith("mlp_")) return `sp_${k}`;
  if (k.startsWith("estoque_divergente_ml")) return k.replace("estoque_divergente_", "estoque_divergente_sp_");
  return k;
};

const GROUPS = [
  {
    id: "geral",
    label: "Geral",
    cols: [
      { key: "sku",                label: "SKU",            fixed: true },
      { key: "overall_status",     label: "Status" },
      { key: "error_count",        label: "Erros" },
      { key: "alert_count",        label: "Alertas" },
      { key: "grupo_preco",        label: "Grupo" },
      { key: "ean_bling",          label: "EAN" },
      { key: "estoque_planilha",   label: "Estq Planilha" },
      { key: "estoque_bling",      label: "Estq Geral" },
      { key: "estoque_bling_fba",  label: "Estq FBA" },
      { key: "estoque_divergente", label: "Estq Div",       type: "simNao" },
      { key: "bling_price_expected",   label: "Preço Bling Esp." },
      { key: "bling_price_actual",     label: "Preço Bling" },
      { key: "bling_price_divergente", label: "Preço Bling Div", type: "simNao" },
      { key: "nome_planilha",             label: "Nome Planilha" },
      { key: "nome_bling",                label: "Nome Bling" },
      { key: "nome_bling_divergente",     label: "Nome Bling Div",        type: "simNao" },
      { key: "nome_planilha_fora_padrao", label: "Planilha Fora Padrão",  type: "simNao" },
      { key: "nome_bling_fora_padrao",    label: "Bling Fora Padrão",     type: "simNao" },
      { key: "planilha_found",     label: "Planilha",       type: "bool" },
      { key: "bling_found",        label: "Bling",          type: "bool" },
    ],
  },
  {
    id: "mlc",
    label: "Mercado Livre",
    foundKey: "mlc_found",
    rowFilter: (row) => !isEgsp(row.sku),
    cols: ML_COLS,
  },
  {
    id: "sp_ml",
    label: "SP-ML",
    foundKey: "sp_mlc_found",
    rowFilter: (row) => isEgsp(row.sku),
    cols: [...ML_COLS.map((c) => ({ ...c, key: toSpKey(c.key) })), ...SP_FULL_COLS],
  },
  {
    id: "shopee",
    label: "Shopee",
    foundKey: "shopee_found",
    rowFilter: (row) => isEgsp(row.sku),
    cols: [
      { key: "sku",                        label: "SKU",          fixed: true },
      { key: "overall_status",             label: "Status" },
      { key: "shopee_found",               label: "Encontrado",   type: "bool" },
      { key: "estoque_bling",              label: "Estq Bling" },
      { key: "shopee_stock",               label: "Estq Shopee" },
      { key: "estoque_divergente_shopee",  label: "Estq Div",     type: "simNao" },
      { key: "shopee_promo_active",          label: "Em Promoção",   type: "simNao" },
      { key: "shopee_list_price_expected",  label: "Lista Esp." },
      { key: "shopee_list_price_actual",    label: "Lista Atual" },
      { key: "shopee_list_price_divergente", label: "Lista Div",    type: "simNao" },
      { key: "shopee_price_expected",       label: "Venda Esp." },
      { key: "shopee_price_actual",         label: "Venda Atual" },
      { key: "shopee_price_divergente",     label: "Venda Div",     type: "simNao" },
      { key: "vinculo_bling_shopee",        label: "Vínculo Bling", type: "bool" },
    ],
  },
  {
    id: "amazon",
    label: "Amazon",
    foundKey: "amazon_found",
    // Sem rowFilter de propósito: desde 2026-10-30 a Amazon vale pras DUAS filiais — matriz em
    // FBA e EGSP* em FBM (shared/channels/amazon_logistica.py). A coluna "Logíst. Esp." diz
    // qual é a esperada de cada linha.
    cols: [
      { key: "sku",                         label: "SKU",           fixed: true },
      { key: "overall_status",              label: "Status" },
      { key: "amazon_found",                label: "Encontrado",    type: "bool" },
      { key: "amazon_is_fba",               label: "FBA",           type: "bool" },
      { key: "amazon_logistica_esperada",   label: "Logíst. Esp." },
      { key: "amazon_logistica_ok",         label: "Logíst. OK",    type: "bool" },
      { key: "amazon_buy_box",              label: "Destaque",      type: "simNao" },
      { key: "estoque_bling",               label: "Estq Bling" },
      { key: "estoque_bling_amazon",        label: "Estq Bling FBA" },
      { key: "amazon_stock",                label: "Estq Amazon" },
      { key: "amazon_fba_disponivel",       label: "FBA disponível" },
      { key: "amazon_fba_a_caminho",        label: "FBA a caminho" },
      { key: "estoque_divergente_amazon",   label: "Estq Div (geral)", type: "simNao" },
      { key: "estoque_divergente_amazon_fba", label: "Estq Div (FBA)", type: "simNao" },
      { key: "amazon_price_expected",       label: "Venda Esp." },
      { key: "amazon_price_actual",         label: "Venda Config." },
      { key: "amazon_price_divergente",     label: "Venda Div",     type: "simNao" },
      { key: "amazon_min_price_expected",   label: "Mín Esp." },
      { key: "amazon_min_price_actual",     label: "Mín Atual" },
      { key: "amazon_price_automatic",      label: "Automático",    type: "simNao" },
      { key: "amazon_regra_ok",         label: "Regra OK",      type: "bool" },
      { key: "amazon_regra_preco",      label: "Regra Preço" },
      { key: "amazon_min_price_divergente", label: "Mín Div",       type: "simNao" },
      { key: "amazon_price_live",           label: "Preço ao Vivo" },
      { key: "amazon_fiscal_ok",            label: "Fiscal OK",     type: "simNao" },
      { key: "amazon_fiscal_pendente",      label: "Fiscal pendente" },
      { key: "issues_amazon",               label: "Issues" },
      { key: "vinculo_bling_amazon",        label: "Vínculo Bling", type: "bool" },
      { key: "vinculo_bling_amazon_fba", label: "Vínc. FBA",  type: "bool" },
    ],
  },
  {
    id: "nvs",
    label: "NVS",
    foundKey: "nvs_found",
    rowFilter: (row) => !isEgsp(row.sku),
    cols: [
      { key: "sku",                    label: "SKU",           fixed: true },
      { key: "overall_status",         label: "Status" },
      { key: "nvs_found",              label: "Encontrado",    type: "bool" },
      { key: "estoque_bling",          label: "Estq Bling" },
      { key: "nvs_stock",              label: "Estq NVS" },
      { key: "estoque_divergente_nvs", label: "Estq Div",      type: "simNao" },
      { key: "nvs_price_expected",     label: "Preço Esp." },
      { key: "nvs_price_actual",       label: "Preço Atual" },
      { key: "nvs_price_divergente",   label: "Preço Div",     type: "simNao" },
      { key: "nvs_promo_price",        label: "Promoção" },
      { key: "nvs_promo_ativa",        label: "Promo Ativa",   type: "simNao" },
      { key: "nvs_ean",                label: "EAN" },
      { key: "nvs_ean_ok",             label: "Tem EAN",       type: "simNao" },
      { key: "nvs_nome",               label: "Nome NVS" },
      { key: "nvs_nome_divergente",    label: "Nome Div",      type: "simNao" },
      { key: "nvs_image_count",        label: "Qtd Imagens" },
      { key: "nvs_image_padronizada",  label: "Img Padronizada", type: "simNao" },
      { key: "nvs_imagem_promo",       label: "Img Promo",     type: "simNao" },
      { key: "nvs_descricao_ok",       label: "Descrição OK",  type: "simNao" },
      { key: "nvs_seo_title",          label: "SEO Título" },
      { key: "nvs_category_expected",  label: "Categoria Esp." },
      { key: "nvs_category_actual",    label: "Categoria Atual" },
      { key: "nvs_category_ok",        label: "Categoria OK",  type: "simNao" },
      { key: "vinculo_bling_nvs",      label: "Vínculo Bling", type: "bool" },
    ],
  },
];

// ─── Comandos disponíveis, agrupados por canal ────────────────────────────────

const COMMAND_GROUPS = [
  {
    id: "geral",
    label: "Geral",
    commands: [
      {
        id: "gdpb",
        label: "GDPB",
        fn: runGdpb,
        options: [
          { key: "update_if_exists", label: "Atualizar se existir", default: true },
          { key: "dry_run",          label: "Simulação (dry-run)" },
          { key: "force_images",     label: "Forçar re-upload de imagens" },
        ],
      },
      {
        id: "sync-images",
        label: "Sync Imagens",
        fn: runSyncImages,
        options: [
          { key: "dry_run",      label: "Simulação (dry-run)" },
          { key: "force_images", label: "Forçar re-upload de imagens" },
        ],
      },
      {
        id: "bling-descriptions",
        label: "Descrições Bling",
        fn: runBlingDescriptions,
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
        ],
      },
      {
        id: "bling-price",
        label: "Preço Bling",
        fn: runBlingPrice,
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
        ],
      },
      {
        id: "product-name",
        label: "Nome (Bling → NVS)",
        fn: runProductName,
        options: [
          { key: "dry_run",  label: "Simulação (dry-run)" },
          { key: "skip_nvs", label: "Só Bling (não propagar pra NVS)" },
        ],
      },
    ],
  },
  {
    id: "ml",
    label: "Mercado Livre",
    commands: [
      {
        id: "ml-premium",
        label: "Criar ML Premium",
        fn: runMlPremium,
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
          { key: "debug",   label: "Debug" },
        ],
      },
      {
        id: "ml-classic",
        label: "Criar ML Clássico",
        fn: (body) => runMlPremium({ ...body, listing_type: "classic" }),
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
          { key: "debug",   label: "Debug" },
        ],
      },
      {
        id: "ml-min-price",
        label: "Preço Mínimo ML",
        fn: runMlMinPrice,
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
        ],
      },
      {
        id: "ml-price",
        label: "Preço Venda ML",
        fn: runMlPrice,
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
        ],
      },
      {
        id: "ml-fiscal",
        label: "Dados Fiscais ML",
        fn: runMlFiscal,
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
        ],
      },
      {
        id: "ml-close",
        label: "Fechar MLP",
        fn: runMlClose,
        options: [
          { key: "confirm", label: "Confirmar fechamento" },
          { key: "excluir", label: "Excluir (em vez de só inativar)" },
        ],
      },
    ],
  },
  {
    id: "shopee",
    label: "Shopee",
    commands: [
      {
        id: "shopee-price",
        label: "Preço Shopee",
        fn: runShopeePrice,
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
        ],
      },
      {
        id: "shopee-attrs",
        label: "Atributos Shopee",
        fn: runShopeeAttrs,
        options: [
          { key: "dry_run",            label: "Simulação (dry-run)" },
          { key: "fix_image_order",    label: "Corrigir ordem das imagens" },
          { key: "no_update_if_exists", label: "Não atualizar se já existem" },
        ],
      },
    ],
  },
  {
    id: "amazon",
    label: "Amazon",
    commands: [
      {
        id: "amz-price",
        label: "Preço Amazon",
        fn: runAmzPrice,
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
          { key: "force",   label: "Forçar atualização (mesmo se já estiver correto)" },
        ],
      },
      {
        id: "amz-stock",
        label: "Estoque Amazon",
        fn: runAmzStock,
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
        ],
      },
      {
        id: "amz-fiscal",
        label: "Dados fiscais Amazon",
        fn: runAmzFiscal,
        options: [
          { key: "dry_run", label: "Simulação (valida na Amazon sem gravar)" },
        ],
      },
    ],
  },
  {
    id: "nvs",
    label: "NVS",
    commands: [
      {
        id: "nvs-price",
        label: "Preço/Estoque NVS",
        fn: runNvsPrice,
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
        ],
      },
      {
        id: "nvs-images",
        label: "Imagens NVS",
        fn: runNvsImages,
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
          { key: "force",   label: "Reclassificar mesmo se já padronizada" },
        ],
      },
      {
        id: "nvs-attrs",
        label: "Marca NVS",
        fn: runNvsAttrs,
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
        ],
      },
      {
        id: "nvs-ean",
        label: "EAN NVS",
        fn: runNvsEan,
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
        ],
      },
      {
        id: "nvs-description",
        label: "Descrição NVS",
        fn: runNvsDescription,
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
        ],
      },
      {
        id: "nvs-category",
        label: "Categoria NVS",
        fn: runNvsCategory,
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
        ],
      },
      {
        id: "nvs-subcategories",
        label: "Subcategoria NVS",
        fn: runNvsSubcategories,
        options: [
          { key: "dry_run", label: "Simulação (dry-run)" },
        ],
      },
    ],
  },
];

// Lista plana — usada pra lookup por id (options, fn, label)
const COMMANDS = COMMAND_GROUPS.flatMap((g) => g.commands);

// ─── Helpers de cor ───────────────────────────────────────────────────────────

function cellClass(key, value) {
  const v = String(value ?? "").trim();
  if (!v || v === "None") return "text-gray-400";

  if (key === "overall_status") {
    if (v === "ERRO")   return "font-semibold text-red-600";
    if (v === "ALERTA") return "font-semibold text-yellow-600";
    if (v === "OK")     return "text-green-600";
  }
  if (key.endsWith("_fora_padrao")) {
    if (v === "SIM") return "font-semibold text-yellow-600";
    if (v === "NÃO") return "text-green-600";
  }
  if (key.includes("divergente") || key.includes("issues")) {
    if (v === "SIM") return "font-semibold text-red-600";
    if (v === "NÃO") return "text-green-600";
    if (v && v !== "") return "text-red-600 text-xs";
  }
  if (key.endsWith("_found") || key.startsWith("vinculo_bling")) {
    if (v === "True" || v === "SIM")  return "text-green-600";
    if (v === "False" || v === "NÃO") return "text-red-500";
  }
  if (key === "nvs_ean_ok") {
    if (v === "NÃO") return "font-semibold text-red-600";
    if (v === "SIM") return "text-green-600";
  }
  if (key === "nvs_promo_ativa") {
    if (v === "SIM") return "font-semibold text-red-600";
    if (v === "NÃO") return "text-green-600";
  }
  if (key === "nvs_seo_title") {
    if (v === "ok") return "text-green-600";
    if (v === "atualizado") return "text-blue-600";
    return "font-semibold text-red-600";
  }
  if (key === "nvs_imagem_promo") {
    if (v === "SIM") return "font-semibold text-yellow-600";
    if (v === "NÃO") return "text-green-600";
  }
  if (key === "nvs_image_padronizada") {
    if (v === "SIM") return "text-green-600";
    if (v === "NÃO") return "font-semibold text-red-600";
  }
  if (key === "amazon_is_fba") {
    if (v === "True") return "text-blue-600 font-semibold";
  }
  // Logística errada pra filial do SKU (matriz FBA / EGSP* FBM) é ERRO — destaca em vermelho
  if (key === "amazon_logistica_ok") {
    if (v === "False") return "font-semibold text-red-600";
    if (v === "True") return "text-green-600";
  }
  if (key === "amazon_buy_box") {
    if (v === "SIM") return "text-green-600 font-semibold";
    if (v === "NÃO") return "text-red-500";
  }
  if (key.endsWith("_automatic")) {
    if (v === "SIM") return "text-green-600";
    if (v === "NÃO") return "text-yellow-600";
  }
  if (key === "error_count"  && v !== "0") return "font-bold text-red-600";
  if (key === "alert_count"  && v !== "0") return "font-bold text-yellow-600";

  return "text-gray-700";
}

function displayValue(value) {
  const v = String(value ?? "").trim();
  if (!v || v === "None") return "—";
  if (v === "True")  return "✓";
  if (v === "False") return "✗";
  return v;
}

function matchesFilter(rawValue, filterVal) {
  if (!filterVal) return true;
  const q = filterVal.toLowerCase();
  const raw = String(rawValue ?? "").toLowerCase();
  const disp = String(displayValue(rawValue)).toLowerCase();
  return raw.includes(q) || disp.includes(q);
}

// ─── Componente de filtro toggle ──────────────────────────────────────────────

function ToggleFilter({ colKey, type, value, onChange }) {
  const options =
    type === "bool"
      ? [{ val: "", label: "Todos" }, { val: "True", label: "✓" }, { val: "False", label: "✗" }]
      : [{ val: "", label: "Todos" }, { val: "SIM", label: "SIM" }, { val: "NÃO", label: "NÃO" }];

  return (
    <div className="flex gap-0.5">
      {options.map((opt) => (
        <button
          key={opt.val}
          onClick={() => onChange(colKey, opt.val === value ? "" : opt.val)}
          className={`px-1.5 py-0.5 text-xs rounded border transition-colors ${
            value === opt.val && opt.val !== ""
              ? "bg-blue-600 text-white border-blue-600"
              : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Tooltip de divergências no Status ───────────────────────────────────────

function StatusCell({ value, divergencias, channelFilter }) {
  const [show, setShow] = useState(false);
  const allMsgs = divergencias ? String(divergencias).split(" | ").filter(Boolean) : [];

  const filters = channelFilter ? [].concat(channelFilter).map((f) => f.toUpperCase()) : [];
  const msgs = filters.length
    ? allMsgs.filter((m) => filters.some((f) => m.toUpperCase().includes(f)))
    : allMsgs;

  // Recalcula status a partir das mensagens do canal ativo
  let v = String(value ?? "").trim();
  if (channelFilter) {
    if (msgs.some((m) => m.includes("[ERRO]")))   v = "ERRO";
    else if (msgs.some((m) => m.includes("[ALERTA]"))) v = "ALERTA";
    else v = "OK";
  }

  const cls = cellClass("overall_status", v);

  if (!msgs.length) {
    return <span className={cls}>{v || "—"}</span>;
  }

  return (
    <span className="relative inline-block">
      <button
        className={`${cls} underline decoration-dotted cursor-help`}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onFocus={() => setShow(true)}
        onBlur={() => setShow(false)}
      >
        {v}
      </button>
      {show && (
        <div className="absolute z-50 left-0 top-full mt-1 w-80 bg-gray-900 text-gray-100 text-xs rounded-lg p-3 shadow-xl space-y-1">
          {msgs.map((m, i) => {
            const isErro = m.startsWith("[ERRO]");
            const isAlerta = m.startsWith("[ALERTA]");
            return (
              <div
                key={i}
                className={isErro ? "text-red-300" : isAlerta ? "text-yellow-300" : "text-gray-300"}
              >
                {m}
              </div>
            );
          })}
        </div>
      )}
    </span>
  );
}

// ─── Seletor de comandos (multi-seleção, agrupado por canal) ─────────────────

function CommandPicker({ selectedIds, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const label =
    selectedIds.size === 0
      ? "Selecionar comandos"
      : selectedIds.size === 1
        ? COMMANDS.find((c) => selectedIds.has(c.id))?.label
        : `${selectedIds.size} comandos selecionados`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white hover:bg-gray-50 flex items-center gap-2 whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {label}
        <span className="text-gray-400 text-xs">▾</span>
      </button>

      {open && (
        <div className="absolute bottom-full mb-1 left-0 w-64 max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-1">
          {COMMAND_GROUPS.map((grp) => (
            <div key={grp.id} className="px-1 mb-1 last:mb-0">
              <div className="px-2 py-1 text-[10px] font-semibold uppercase text-gray-400 tracking-wide">
                {grp.label}
              </div>
              {grp.commands.map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer rounded"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(c.id)}
                    onChange={() => onToggle(c.id)}
                    className="rounded"
                  />
                  {c.label}
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Painel de ação ───────────────────────────────────────────────────────────

function ActionPanel({ selectedSkus, onClear }) {
  const [selectedCmdIds, setSelectedCmdIds] = useState(new Set());
  const [optionsByCmd, setOptionsByCmd]     = useState({});
  const [jobs, setJobs]         = useState([]); // [{ cmdId, label, jobId }]
  const [running, setRunning]   = useState(false);
  const [error, setError]       = useState("");

  const toggleCmd = (id) => {
    setSelectedCmdIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        const c = COMMANDS.find((x) => x.id === id);
        setOptionsByCmd((prevOpts) => ({
          ...prevOpts,
          [id]: Object.fromEntries((c?.options || []).map((o) => [o.key, o.default ?? false])),
        }));
      }
      return next;
    });
  };

  const toggleOption = (cmdId, key) =>
    setOptionsByCmd((prev) => ({
      ...prev,
      [cmdId]: { ...prev[cmdId], [key]: !prev[cmdId]?.[key] },
    }));

  const activeCmds = COMMANDS.filter((c) => selectedCmdIds.has(c.id));

  const run = async () => {
    if (activeCmds.length === 0) return;
    setError("");
    setRunning(true);
    setJobs([]);

    const started = [];
    const errors = [];

    for (const cmd of activeCmds) {
      try {
        const body = { skus: Array.from(selectedSkus), ...(optionsByCmd[cmd.id] || {}) };
        const { data } = await cmd.fn(body);
        started.push({ cmdId: cmd.id, label: cmd.label, jobId: data.job_id });
      } catch (e) {
        errors.push(`${cmd.label}: ${e.response?.data?.detail || e.message}`);
      }
    }

    setJobs(started);
    setError(errors.join(" | "));
    setRunning(false);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg">
      <div className="max-w-screen-xl mx-auto px-4 py-3 space-y-3">
        {/* Linha de controles */}
        <div className="flex items-center flex-wrap gap-3">
          {/* Contagem + limpar */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-blue-800">
              {selectedSkus.size} SKU{selectedSkus.size !== 1 ? "s" : ""} selecionado{selectedSkus.size !== 1 ? "s" : ""}
            </span>
            <button
              onClick={onClear}
              className="text-xs text-gray-400 hover:text-gray-600 underline"
            >
              limpar
            </button>
          </div>

          <span className="text-gray-300">|</span>

          <CommandPicker selectedIds={selectedCmdIds} onToggle={toggleCmd} />

          {/* Botão executar */}
          <button
            onClick={run}
            disabled={activeCmds.length === 0 || running}
            className="ml-auto bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-semibold px-5 py-1.5 rounded-lg transition-colors whitespace-nowrap"
          >
            {running ? "Iniciando..." : activeCmds.length > 1 ? `Executar (${activeCmds.length})` : "Executar"}
          </button>
        </div>

        {/* Opções de cada comando selecionado */}
        {activeCmds.some((c) => c.options?.length) && (
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {activeCmds.filter((c) => c.options?.length).map((c) => (
              <div key={c.id} className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-gray-500 whitespace-nowrap">{c.label}:</span>
                {c.options.map((opt) => (
                  <label key={opt.key} className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={optionsByCmd[c.id]?.[opt.key] ?? false}
                      onChange={() => toggleOption(c.id, opt.key)}
                      className="rounded"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            ))}
          </div>
        )}

        {error && <p className="text-red-500 text-xs">{error}</p>}

        {/* Log de cada job disparado — só esta área rola, o resto do painel fica fixo */}
        {jobs.length > 0 && (
          <div className="max-h-[45vh] overflow-y-auto space-y-3 pr-1">
            {jobs.map((j) => (
              <div key={j.jobId}>
                <p className="text-xs font-semibold text-gray-600">{j.label}</p>
                <JobLog jobId={j.jobId} onDone={() => {}} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Copiar EANs dos produtos sem anúncio no canal ────────────────────────────

const ausente = (v) => v === "False" || v === "NÃO" || v === false;

function CopiarEansAusentes({ rows, foundKey, canal }) {
  const [msg, setMsg] = useState("");
  const [mostrar, setMostrar] = useState(false);
  const ausentes = rows.filter((r) => ausente(r[foundKey]));
  const eans = [...new Set(ausentes.map((r) => String(r.ean_bling ?? "").trim()).filter((e) => e && e !== "nan"))];
  const semEan = ausentes.filter((r) => !String(r.ean_bling ?? "").trim() || r.ean_bling === "nan").map((r) => r.sku);
  const texto = eans.join("\n");

  const copiar = async () => {
    try { await navigator.clipboard.writeText(texto); setMsg(`${eans.length} EAN(s) copiados`); }
    catch { setMsg("Não consegui copiar — use a lista abaixo"); setMostrar(true); }
    setTimeout(() => setMsg(""), 4000);
  };

  return (
    <div className="bg-white rounded-xl shadow px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-gray-700">
          <strong>{ausentes.length}</strong> produto(s) sem anúncio em <strong>{canal}</strong>
          {semEan.length > 0 && <span className="text-orange-600"> · {semEan.length} sem EAN no Bling</span>}
        </span>
        <button type="button" onClick={copiar} disabled={!eans.length}
          className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold px-4 py-1.5 rounded-lg">
          Copiar {eans.length} EAN(s) dos ausentes
        </button>
        <button type="button" onClick={() => setMostrar((m) => !m)} className="text-blue-700 hover:underline text-xs">
          {mostrar ? "esconder lista" : "ver lista"}
        </button>
        {msg && <span className="text-green-700">{msg}</span>}
      </div>
      {mostrar && (
        <div className="mt-2 grid md:grid-cols-2 gap-3">
          <textarea readOnly value={texto} rows={Math.min(10, Math.max(3, eans.length))}
            className="w-full font-mono text-xs border border-gray-200 rounded p-2" />
          {semEan.length > 0 && (
            <div className="text-xs text-gray-600">
              <p className="font-medium text-orange-700 mb-1">Sem EAN no Bling (não entram na cópia):</p>
              <p className="font-mono break-words">{semEan.join(", ")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function GVCResultados() {
  const [data, setData]               = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [group, setGroup]             = useState("geral");
  const [filters, setFilters]         = useState({});
  const [error, setError]             = useState("");
  const [selectedSkus, setSelectedSkus] = useState(new Set());
  const filterRefs                    = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: res } = await getGvcCsv();
      setData(res.rows ?? []);
      setGeneratedAt(res.generated_at ?? null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cols = useMemo(
    () => GROUPS.find((g) => g.id === group)?.cols ?? [],
    [group]
  );

  const filtered = useMemo(() => {
    const rowFilter = GROUPS.find((g) => g.id === group)?.rowFilter;
    const base = rowFilter ? data.filter(rowFilter) : data;
    return base.filter((row) =>
      Object.entries(filters).every(([key, val]) => {
        if (!val) return true;
        const col = GROUPS.flatMap((g) => g.cols).find((c) => c.key === key);
        if (col?.type === "bool" || col?.type === "simNao") {
          return String(row[key] ?? "") === val;
        }
        return matchesFilter(row[key], val);
      })
    );
  }, [data, filters, group]);

  const setFilter = (key, val) =>
    setFilters((prev) => ({ ...prev, [key]: val }));

  const clearFilters = () => {
    setFilters({});
    Object.values(filterRefs.current).forEach((el) => { if (el) el.value = ""; });
  };

  const activeFilters = Object.values(filters).filter(Boolean).length;

  // ── Seleção de SKUs ──────────────────────────────────────────────────────────
  const filteredSkus = useMemo(() => filtered.map((r) => r.sku).filter(Boolean), [filtered]);
  const allVisibleSelected = filteredSkus.length > 0 && filteredSkus.every((s) => selectedSkus.has(s));
  const someVisibleSelected = filteredSkus.some((s) => selectedSkus.has(s));

  const toggleSku = (sku) =>
    setSelectedSkus((prev) => {
      const next = new Set(prev);
      next.has(sku) ? next.delete(sku) : next.add(sku);
      return next;
    });

  const toggleAllVisible = () => {
    setSelectedSkus((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        filteredSkus.forEach((s) => next.delete(s));
      } else {
        filteredSkus.forEach((s) => next.add(s));
      }
      return next;
    });
  };

  const hasSelection = selectedSkus.size > 0;

  return (
    <div className={`space-y-4 ${hasSelection ? "pb-48" : ""}`}>
      {/* Cabeçalho */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <h2 className="text-2xl font-bold text-gray-800">GVC — Resultados Detalhados</h2>
            {generatedAt && (
              <span className="text-sm text-gray-400">gerado em {generatedAt}</span>
            )}
          </div>
          {!loading && (
            <p className="text-sm text-gray-500 mt-0.5">
              {filtered.length} de {data.length} registros
              {activeFilters > 0 && (
                <button onClick={clearFilters} className="ml-3 text-blue-600 hover:underline text-xs">
                  limpar {activeFilters} filtro{activeFilters > 1 ? "s" : ""}
                </button>
              )}
            </p>
          )}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-sm bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg disabled:opacity-50"
        >
          {loading ? "Carregando..." : "↻ Atualizar"}
        </button>
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}

      {/* Abas de grupo */}
      <div className="flex gap-1 bg-white rounded-xl shadow px-2 py-1 w-fit">
        {GROUPS.map((g) => (
          <button
            key={g.id}
            onClick={() => setGroup(g.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              group === g.id
                ? "bg-blue-800 text-white"
                : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"
            }`}
          >
            {g.label}
          </button>
        ))}
      </div>

      {/* Copiar EANs dos produtos sem anúncio no canal da aba (respeita os filtros aplicados) */}
      {!loading && GROUPS.find((g) => g.id === group)?.foundKey && (
        <CopiarEansAusentes
          rows={filtered}
          foundKey={GROUPS.find((g) => g.id === group).foundKey}
          canal={GROUPS.find((g) => g.id === group).label}
        />
      )}

      {/* Tabela */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">Carregando dados...</div>
        ) : data.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            Nenhum resultado disponível. Execute uma auditoria GVC primeiro.
          </div>
        ) : (
          <div className="overflow-auto max-h-[70vh] scrollbar-visible">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10">
                {/* Labels */}
                <tr className="bg-gray-50 border-b border-gray-200">
                  {/* Checkbox select-all */}
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      ref={(el) => { if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected; }}
                      onChange={toggleAllVisible}
                      className="rounded cursor-pointer"
                      title="Selecionar todos visíveis"
                    />
                  </th>
                  {cols.map((col) => (
                    <th
                      key={col.key}
                      className="px-3 py-2 text-left text-xs font-semibold text-gray-600 whitespace-nowrap"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
                {/* Filtros */}
                <tr className="bg-gray-50 border-b border-gray-300">
                  <th className="px-3 py-1" />
                  {cols.map((col) => (
                    <th key={col.key} className="px-2 py-1">
                      {col.type === "bool" || col.type === "simNao" ? (
                        <ToggleFilter
                          colKey={col.key}
                          type={col.type}
                          value={filters[col.key] || ""}
                          onChange={setFilter}
                        />
                      ) : (
                        <input
                          ref={(el) => (filterRefs.current[col.key] = el)}
                          type="text"
                          placeholder="filtrar..."
                          defaultValue={filters[col.key] || ""}
                          onChange={(e) => setFilter(col.key, e.target.value)}
                          className="w-full min-w-[80px] text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 font-normal"
                        />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={cols.length + 1} className="px-4 py-8 text-center text-gray-400">
                      Nenhum registro corresponde aos filtros aplicados.
                    </td>
                  </tr>
                ) : (
                  filtered.map((row, i) => {
                    const sku = row.sku;
                    const isSelected = selectedSkus.has(sku);
                    return (
                      <tr
                        key={i}
                        onClick={() => sku && toggleSku(sku)}
                        className={`cursor-pointer transition-colors ${
                          isSelected
                            ? "bg-blue-50 hover:bg-blue-100"
                            : row.overall_status === "ERRO"   ? "bg-red-50 hover:bg-red-100"
                            : row.overall_status === "ALERTA" ? "bg-yellow-50 hover:bg-yellow-100"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        {/* Checkbox */}
                        <td className="px-3 py-1.5 w-8" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => sku && toggleSku(sku)}
                            className="rounded cursor-pointer"
                          />
                        </td>
                        {cols.map((col) => (
                          <td
                            key={col.key}
                            className={`px-3 py-1.5 whitespace-nowrap text-xs ${
                              col.key === "sku" ? "font-mono font-medium text-gray-800" :
                              col.key === "issues_amazon" ? "max-w-xs truncate" :
                              col.key === "overall_status" ? "" :
                              cellClass(col.key, row[col.key])
                            }`}
                            title={col.key === "issues_amazon" ? String(row[col.key] ?? "") : undefined}
                          >
                            {col.key === "overall_status" ? (
                              <StatusCell
                                value={row[col.key]}
                                divergencias={row.divergencias}
                                channelFilter={CHANNEL_PREFIX[group] ?? null}
                              />
                            ) : (
                              displayValue(row[col.key])
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Rodapé */}
        {!loading && filtered.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">
            {filtered.length} registro{filtered.length !== 1 ? "s" : ""} exibido{filtered.length !== 1 ? "s" : ""}
            {activeFilters > 0 && ` (${data.length - filtered.length} oculto${data.length - filtered.length !== 1 ? "s" : ""} por filtros)`}
            {selectedSkus.size > 0 && (
              <span className="ml-3 text-blue-600 font-medium">
                {selectedSkus.size} selecionado{selectedSkus.size !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Painel de ação (fixo no rodapé quando há seleção) */}
      {hasSelection && (
        <ActionPanel
          selectedSkus={selectedSkus}
          onClear={() => setSelectedSkus(new Set())}
        />
      )}
    </div>
  );
}
